import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Cell, ComposedChart, Line, Legend } from "recharts";
import { TrendingUp, DollarSign, Activity, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

export default function Profitability() {
    const [data, setData] = useState(null);
    const [history, setHistory] = useState(null);
    const [historyMonths, setHistoryMonths] = useState(12);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/dashboard/profitability");
                if (r?.data) setData(r.data);
            } catch (e) { console.warn(e); }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get(`/dashboard/profitability-history?months=${historyMonths}`);
                if (r?.data) setHistory(r.data);
            } catch (e) { console.warn(e); }
        })();
    }, [historyMonths]);

    if (!data) return <div className="text-sm text-slate-500 font-mono">Cargando rentabilidad...</div>;

    return (
        <div className="space-y-4" data-testid="profitability-page">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="fm-card p-4">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Ingresos totales</div>
                    <div className="font-mono font-black text-2xl tabular-nums">{money(data.total_revenue)}</div>
                </div>
                <div className="fm-card p-4">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Costos estimados</div>
                    <div className="font-mono font-black text-2xl tabular-nums text-amber-600">{money(data.total_cost)}</div>
                </div>
                <div className="fm-card p-4 border-l-4 border-l-emerald-500">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Margen total</div>
                    <div className="font-mono font-black text-2xl tabular-nums text-emerald-700">{money(data.total_margin)}</div>
                </div>
                <div className="fm-card p-4 border-l-4 border-l-red-600">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Margen promedio</div>
                    <div className="font-mono font-black text-2xl tabular-nums">{data.avg_margin_pct.toFixed(1)}%</div>
                </div>
            </div>

            {/* === HISTÓRICO DE RENTABILIDAD MENSUAL === */}
            {history && (
                <div className="fm-card p-5" data-testid="profitability-history">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <h3 className="font-display font-bold text-base flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-indigo-600" /> Histórico de Rentabilidad
                            {history.trend && (
                                <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-sm font-mono ml-2 ${history.trend.direction === "up" ? "bg-emerald-100 text-emerald-800" : history.trend.direction === "down" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>
                                    {history.trend.direction === "up" && <ArrowUpRight className="w-3 h-3" />}
                                    {history.trend.direction === "down" && <ArrowDownRight className="w-3 h-3" />}
                                    {history.trend.direction === "flat" && <Minus className="w-3 h-3" />}
                                    {history.trend.delta_pct_points >= 0 ? "+" : ""}{history.trend.delta_pct_points.toFixed(1)} pts vs inicio
                                </span>
                            )}
                        </h3>
                        <select
                            value={historyMonths}
                            onChange={(e) => setHistoryMonths(parseInt(e.target.value, 10))}
                            className="fm-input text-xs w-auto"
                            data-testid="history-months-select"
                        >
                            <option value={3}>Últimos 3 meses</option>
                            <option value={6}>Últimos 6 meses</option>
                            <option value={12}>Últimos 12 meses</option>
                            <option value={24}>Últimos 24 meses</option>
                        </select>
                    </div>
                    <ResponsiveContainer width="100%" height={320}>
                        <ComposedChart data={history.timeline}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                            <YAxis yAxisId="left" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v.toFixed(0)}%`} domain={[0, 100]} />
                            <Tooltip
                                formatter={(v, name) => name === "margin_pct" ? `${Number(v).toFixed(1)}%` : money(v)}
                                labelStyle={{ fontWeight: 700 }}
                            />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar yAxisId="left" dataKey="revenue" name="Ingresos" fill="#3b82f6" radius={0} />
                            <Bar yAxisId="left" dataKey="cost" name="Costos" fill="#f59e0b" radius={0} />
                            <Bar yAxisId="left" dataKey="margin" name="Margen" fill="#10b981" radius={0} />
                            <Line yAxisId="right" type="monotone" dataKey="margin_pct" name="% Margen" stroke="#dc2626" strokeWidth={2} dot={{ r: 4 }} />
                        </ComposedChart>
                    </ResponsiveContainer>
                    <div className="mt-3 overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200">
                                    {["Mes", "Servicios", "Ingresos", "Costos", "Margen", "% Margen"].map((h) => (
                                        <th key={h} className="text-left py-2 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {history.timeline.map((t) => (
                                    <tr key={t.period} className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="py-1.5 px-3 font-bold">{t.label}</td>
                                        <td className="py-1.5 px-3 font-mono">{t.service_count}</td>
                                        <td className="py-1.5 px-3 text-right font-mono">{money(t.revenue)}</td>
                                        <td className="py-1.5 px-3 text-right font-mono text-amber-700">{money(t.cost)}</td>
                                        <td className="py-1.5 px-3 text-right font-mono font-bold text-emerald-700">{money(t.margin)}</td>
                                        <td className="py-1.5 px-3 text-right">
                                            <span className={`fm-badge ${t.margin_pct > 60 ? "fm-badge-positive" : t.margin_pct > 40 ? "fm-badge-warning" : t.margin_pct > 0 ? "fm-badge-neutral" : "fm-badge-critical"}`}>
                                                {t.margin_pct.toFixed(1)}%
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="fm-card p-5">
                    <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Margen por cliente</h3>                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={data.by_client.slice(0, 10)} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                            <YAxis type="category" dataKey="client_name" tick={{ fontSize: 11 }} width={150} />
                            <Tooltip formatter={(v) => money(v)} />
                            <Bar dataKey="margin" fill="#10b981" radius={0} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div className="fm-card p-5">
                    <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2"><Activity className="w-4 h-4" /> % Margen por tipo de servicio</h3>
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={data.by_service_type.slice(0, 10)}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="code" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v.toFixed(0)}%`} />
                            <Tooltip formatter={(v, n) => n === "margin_pct" ? `${v.toFixed(1)}%` : money(v)} />
                            <Bar dataKey="margin_pct" radius={0}>
                                {data.by_service_type.map((_, i) => (
                                    <Cell key={i} fill={data.by_service_type[i].margin_pct > 60 ? "#10b981" : data.by_service_type[i].margin_pct > 40 ? "#f59e0b" : "#dc2626"} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="fm-card overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-200">
                    <h3 className="font-display font-bold text-base">Detalle por cliente</h3>
                </div>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                            {["Cliente", "Servicios", "Ingresos", "Costo est.", "Margen", "%", ""].map((h) => (
                                <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.by_client.map((c) => (
                            <tr key={c.client_id} className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="py-2 px-3 font-medium">{c.client_name}</td>
                                <td className="py-2 px-3 font-mono text-xs">{c.service_count}</td>
                                <td className="py-2 px-3 text-right font-mono">{money(c.revenue)}</td>
                                <td className="py-2 px-3 text-right font-mono text-amber-700">{money(c.cost)}</td>
                                <td className="py-2 px-3 text-right font-mono font-bold text-emerald-700">{money(c.margin)}</td>
                                <td className="py-2 px-3 text-right">
                                    <span className={`fm-badge ${c.margin_pct > 60 ? "fm-badge-positive" : c.margin_pct > 40 ? "fm-badge-warning" : "fm-badge-critical"}`}>{c.margin_pct.toFixed(1)}%</span>
                                </td>
                                <td className="py-2 px-3">
                                    <div className="w-24 h-2 bg-slate-100 rounded-sm overflow-hidden">
                                        <div className={`h-full ${c.margin_pct > 60 ? "bg-emerald-500" : c.margin_pct > 40 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${Math.min(100, Math.max(0, c.margin_pct))}%` }} />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
